import { useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DocumentDuplicateIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../shared/components/ProtectedRoute'
import UserHeader from '../components/UserHeader'
import SubscriptionInfo from '../components/subscription/SubscriptionInfo'
import PlanComparison from '../components/subscription/PlanComparison'
import QuickActions from '../components/dashboard/QuickActions'
import StatsCards from '../components/dashboard/StatsCards'
import RecentUrlsTable from '../components/dashboard/RecentUrlsTable'
import UrlShortenerPanel from '../components/dashboard/UrlShortenerPanel'
import { toDashboardUrls, useDashboardData } from '../hooks/useDashboardData'
import { useTranslation } from 'react-i18next'
import { Z_CLASS } from '../../../shared/components/layout/zLayers'

const COPY_TOAST_DURATION_MS = 2000

interface SectionErrorProps {
  message: string
  onRetry: () => void
}

/** 섹션 단위 오류 — 한 쿼리가 실패해도 나머지 화면은 계속 쓸 수 있게 한다 */
function SectionError({ message, onRetry }: SectionErrorProps) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 dark:border-rose-900 dark:bg-rose-950/30">
      <div className="flex items-start gap-3">
        <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
        <p className="text-sm text-rose-800 dark:text-rose-200">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700"
      >
        {t('common.retry')}
      </button>
    </div>
  )
}

function SkeletonCards({ count }: { count: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="h-32 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-700"
        />
      ))}
    </div>
  )
}

/** 사용자 대시보드 */
export default function DashboardPage() {
  const { t } = useTranslation()
  const {
    urlsQuery,
    statsQuery,
    subscriptionQuery,
    usageQuery,
    availablePlansQuery,
  } = useDashboardData()
  const { user } = useAuth()

  const [copySuccess, setCopySuccess] = useState<string | null>(null)
  const [showPlanComparison, setShowPlanComparison] = useState(false)
  const [showPaymentNotice, setShowPaymentNotice] = useState(false)

  // 핸들러를 렌더마다 새로 만들면 아래 memo 가 무력해진다.
  // 모달을 열고 닫을 때마다 목록·지표가 통째로 다시 그려져 화면이 깜빡인다.
  const handleCopySuccess = useCallback((text: string) => {
    setCopySuccess(text)
    setTimeout(() => setCopySuccess(null), COPY_TOAST_DURATION_MS)
  }, [])

  // 결제 연동 전까지는 안내 모달만 노출한다
  const handleUpgrade = useCallback(() => setShowPaymentNotice(true), [])
  const handleManageSubscription = useCallback(
    () => setShowPaymentNotice(true),
    []
  )

  const urls = toDashboardUrls(
    urlsQuery.data?.urls as unknown as Array<Record<string, unknown>>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <UserHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle', {
          name: user?.name || user?.username || '',
        })}
      />

      <div className="mx-auto flex max-w-[1280px] flex-col gap-5 px-6 pb-16 pt-8">
        {/* 링크 단축 */}
        <UrlShortenerPanel onCopySuccess={handleCopySuccess} />

        {/* 요약 지표 */}
        {statsQuery.isError ? (
          <SectionError
            message={t('dashboard.statsError')}
            onRetry={() => statsQuery.refetch()}
          />
        ) : statsQuery.isLoading ? (
          <SkeletonCards count={4} />
        ) : (
          <StatsCards stats={statsQuery.data} />
        )}

        {/* 최근 링크 옆에 구독과 바로가기를 둔다.
            최근 링크는 가로로 넓어야 읽히므로 더 크게 자란다. */}
        <div className="flex flex-wrap items-start gap-5">
          <section className="min-w-0 flex-[999_1_600px]">
            {urlsQuery.isError ? (
              <SectionError
                message={t('dashboard.linksError')}
                onRetry={() => urlsQuery.refetch()}
              />
            ) : urlsQuery.isLoading ? (
              <div className="h-64 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-700" />
            ) : (
              <RecentUrlsTable urls={urls} onCopySuccess={handleCopySuccess} />
            )}
          </section>

          <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-5">
            {subscriptionQuery.isLoading ? (
              <div className="h-80 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-700" />
            ) : subscriptionQuery.data ? (
              <SubscriptionInfo
                data={subscriptionQuery.data}
                usageData={usageQuery.data ?? undefined}
                onUpgrade={handleUpgrade}
                onManage={handleManageSubscription}
              />
            ) : (
              <SectionError
                message={t('dashboard.subscriptionError')}
                onRetry={() => subscriptionQuery.refetch()}
              />
            )}

            {/* 플랜은 구독 응답을 우선 쓴다. localStorage 의 user 는 로그인
                시점 값이라 플랜 변경 후 낡을 수 있다. */}
            <QuickActions
              plan={subscriptionQuery.data?.currentPlan?.type ?? user?.plan}
            />
          </aside>
        </div>
      </div>

      {/* 복사 완료 알림 */}
      <AnimatePresence>
        {copySuccess && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            role="status"
            className={`fixed bottom-4 right-4 ${Z_CLASS.toast} flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-3 text-white shadow-lg`}
          >
            <DocumentDuplicateIcon className="h-5 w-5" />
            {t('dashboard.copied')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 플랜 비교 모달 */}
      <AnimatePresence>
        {showPlanComparison && availablePlansQuery.data && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPlanComparison(false)}
            className={`fixed inset-0 ${Z_CLASS.overlay} flex items-center justify-center bg-black/50 p-4`}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={event => event.stopPropagation()}
              className="max-h-[90vh] w-full max-w-7xl overflow-y-auto"
            >
              <PlanComparison
                data={availablePlansQuery.data}
                onClose={() => setShowPlanComparison(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 결제 준비 안내 */}
      <AnimatePresence>
        {showPaymentNotice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPaymentNotice(false)}
            className={`fixed inset-0 ${Z_CLASS.overlay} flex items-center justify-center bg-black/50 p-4`}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="payment-notice-title"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={event => event.stopPropagation()}
              className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800"
            >
              <div className="mb-4 flex items-center gap-3">
                <InformationCircleIcon className="h-6 w-6 text-primary-500" />
                <h3
                  id="payment-notice-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                  {t('dashboard.paymentTitle')}
                </h3>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('dashboard.paymentBody')}
              </p>
              <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-900 dark:bg-primary-950/30">
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  {t('dashboard.paymentNote')}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowPaymentNotice(false)}
                className="mt-6 w-full rounded-xl bg-primary-600 px-4 py-2.5 font-medium text-white transition hover:bg-primary-700"
              >
                {t('common.confirm')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
